import React, { useCallback, useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';

import { Box, Button, Heading, Paragraph, Text } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import { AutoCompleteBreeds } from '@rover/react-lib/src/components/forms/AddPetForm/AddPetFormFlow/PetFormFlowAddDog/AutoCompleteBreeds';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { Pet } from '@rover/types/src/Pet';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import InlinePetCounter from '../PetCounter/InlinePetCounter';

import {
  ColorfulPerfectMatchBreedSelectorInteraction,
  ColorfulPerfectMatchFieldInteraction,
} from './analyticsEvents';
import { getPetTypeFromCounts } from './petCountUtils';

type PetTypeOption = 'dog' | 'cat';

type Props = {
  isOpen: boolean;
  onClose: (saved: boolean) => void;
  petType: PetTypeOption;
  searchFilters: SearchFilters;
  pets: Pet[];
  dogCount: number;
  catCount: number;
  puppyCount: number;
  onChange: (filters: Partial<SearchFilters>) => void;
  breeds: string[];
  onBreedsChange: (breeds: string[]) => void;
};

const PetDetailsBottomSheet = ({
  isOpen,
  onClose,
  petType,
  searchFilters,
  pets,
  dogCount,
  catCount,
  puppyCount,
  onChange,
  breeds,
  onBreedsChange,
}: Props): JSX.Element => {
  const [isBreedsFocused, setIsBreedsFocused] = useState(false);

  useEffect(() => {
    if (isOpen) setIsBreedsFocused(false);
  }, [isOpen]);

  const handlePetCountChange = useCallback(
    (field: 'dogCount' | 'catCount' | 'puppyCount') => (value: React.SetStateAction<number>) => {
      const currentCounts = { dogCount, catCount, puppyCount };
      const newValue = typeof value === 'function' ? value(currentCounts[field]) : value;
      const newCounts = { ...currentCounts, [field]: newValue };
      const petTypeArray = getPetTypeFromCounts(
        newCounts.dogCount,
        newCounts.catCount,
        newCounts.puppyCount
      );
      onChange({ [field]: newValue, petType: petTypeArray });
    },
    [dogCount, catCount, puppyCount, onChange]
  );

  const handleBreedsChange = useCallback(
    (values: string[]) => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'breed' }));
      onBreedsChange(values);
    },
    [onBreedsChange]
  );

  const handleApply = (): void => {
    if (breeds.length > 0) {
      emitAnalyticsEvent(new ColorfulPerfectMatchBreedSelectorInteraction({ breeds }));
    }
    onClose(true);
  };

  const isDogFlow = petType === 'dog';
  const title = isDogFlow ? t`Your dogs` : t`Your cats`;
  const kibblePetType = isDogFlow ? PetType.DOG : PetType.CAT;

  const noop = (): void => {};
  const petCounterProps = isDogFlow
    ? {
        dogs: dogCount,
        cats: 0,
        puppies: puppyCount,
        availablePets: [PetType.DOG, PetType.PUPPY] as PetType[],
        setDogs: handlePetCountChange('dogCount'),
        setPuppies: handlePetCountChange('puppyCount'),
        setCats: noop,
        pets: pets.filter((pet) => pet.petType === PetType.DOG || pet.petType === PetType.PUPPY),
      }
    : {
        dogs: 0,
        cats: catCount,
        puppies: 0,
        availablePets: [PetType.CAT] as PetType[],
        setDogs: noop,
        setPuppies: noop,
        setCats: handlePetCountChange('catCount'),
        pets: pets.filter((pet) => pet.petType === PetType.CAT),
      };

  return (
    <BottomSheet
      isOpen={isOpen}
      onRequestClose={() => onClose(false)}
      closeOnDragDown
      headerId="pet-details-title"
      contentPaddingStyles={{ padding: '0x' }}
      overflowY="auto"
    >
      <Box px="4x" pb="20x">
        <Heading id="pet-details-title" size="300" mb="4x">
          {title}
        </Heading>

        <InlinePetCounter
          triggeredFrom="Search"
          subtitleText={null}
          searchFilters={searchFilters}
          onPetSelectionChange={onChange}
          titleText={t`How many do you have?`}
          iconVariant="color"
          {...petCounterProps}
        />
        <Box
          mt="4x"
          webProps={{ onFocus: () => setIsBreedsFocused(true) }}
          height={isBreedsFocused ? '40vh' : 'auto'}
        >
          <Text size="200" mb="1x" fontWeight="semibold" display="block">
            {t`What breeds?`}
          </Text>
          <Paragraph size="100" mb="1x" textColor="tertiary">
            {t`Select all that apply.`}
          </Paragraph>
          <AutoCompleteBreeds
            values={breeds}
            onChange={handleBreedsChange}
            petType={kibblePetType}
            hideTitle
          />
        </Box>
      </Box>

      <Box
        position="absolute"
        bottom="0"
        right="0"
        left="0"
        backgroundColor="white"
        p="4x"
        pb="6x"
        borderTop="1px solid"
        borderTopColor="separator.primary"
        zIndex="999"
      >
        <Button variant="primary" fullWidth onPress={handleApply}>
          {t`Apply`}
        </Button>
      </Box>
    </BottomSheet>
  );
};

export default PetDetailsBottomSheet;
